import type { ReactNode } from 'react';

import type { Rarity } from '@sorare/core/src/__generated__/globalTypes';
import { PillButton } from '@sorare/core/src/atoms/buttons/PillButton';
import { Scarcities } from '@sorare/core/src/atoms/icons/Scarcities';
import { Horizontal } from '@sorare/core/src/atoms/layout/flex';
import { Badge } from '@sorare/core/src/atoms/ui/Badge';
import { formatScarcity } from '@sorare/core/src/lib/cards';
import { getRarityRGBColor } from '@sorare/core/src/lib/colors';

type Props<T extends Rarity> = {
  rarities:
    | T[]
    | {
        value: T;
        badgeContent?: ReactNode;
      }[];
  selectedRarity: T | undefined;
  onChange: (rarity: T) => void;
  viewTransitionPrefix?: string;
};

export const PillSwitchRarity = <T extends Rarity>({
  rarities: raritiesProps,
  selectedRarity,
  onChange,
  viewTransitionPrefix,
}: Props<T>) => {
  const rarities = raritiesProps.map(r =>
    typeof r === 'object' ? r : { value: r, badgeContent: undefined }
  );

  return (
    <Horizontal gap={1} center>
      {rarities.map(({ value: rarity, badgeContent }) => {
        const selected = rarity === selectedRarity;
        const scarcity = formatScarcity(rarity);

        return (
          <Badge key={rarity} badgeContent={badgeContent} size="small">
            <PillButton
              size="medium"
              brand
              selected={selected}
              startIcon={<Scarcities rarities={[rarity]} />}
              aria-label={scarcity}
              accentRgb={getRarityRGBColor(rarity)}
              viewTransitionName={
                viewTransitionPrefix
                  ? `${viewTransitionPrefix}-${rarity}`
                  : undefined
              }
              onClick={() => onChange(rarity)}
            >
              {selected ? scarcity : null}
            </PillButton>
          </Badge>
        );
      })}
    </Horizontal>
  );
};
